'use client';
import React from 'react';
import Modal from '@/components/ui/Modal';
import Badge from '@/components/ui/Badge';
import { Customer } from './customerTypes';
import { Phone, MapPin, Cpu, Calendar, DollarSign, Lock, User } from 'lucide-react';

interface Props {
  customer: Customer;
  onClose: () => void;
}

export default function CustomerDetailModal({ customer, onClose }: Props) {
  const lockInPct = Math.min(100, (customer.lockInMonths / 36) * 100);
  const lockInRemaining = 36 - customer.lockInMonths;

  const contractBadgeMap: Record<string, 'active' | 'warning' | 'inactive'> = {
    active: 'active',
    suspended: 'warning',
    expired: 'inactive',
  };

  return (
    <Modal open onClose={onClose} title="Customer Profile" size="lg">
      {/* Header */}
      <div className="flex items-start gap-4 mb-5 pb-5 border-b border-border">
        <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center text-primary-foreground text-xl font-700 shrink-0">
          {customer.fullName.split(' ').map((n) => n[0]).slice(0, 2).join('')}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-700 text-foreground">{customer.fullName}</h3>
            <Badge variant={contractBadgeMap[customer.contractStatus]}>
              {customer.contractStatus.charAt(0).toUpperCase() + customer.contractStatus.slice(1)}
            </Badge>
          </div>
          <span className="font-mono text-sm text-primary font-600 bg-secondary px-2 py-0.5 rounded mt-1 inline-block">
            {customer.customerNumber}
          </span>
          <p className="text-sm text-muted-foreground mt-1">{customer.email}</p>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <InfoRow icon={<Phone size={14} />} label="Mobile" value={customer.mobile} />
        <InfoRow icon={<MapPin size={14} />} label="Address" value={customer.address} />
        <InfoRow icon={<MapPin size={14} />} label="Landmark" value={customer.landmark || '—'} />
        <InfoRow icon={<Cpu size={14} />} label="Machine Model" value={customer.machineModel} />
        <InfoRow icon={<DollarSign size={14} />} label="Monthly Fee" value={`₱${customer.monthlyFee.toLocaleString()}`} />
        <InfoRow icon={<Calendar size={14} />} label="Service Start" value={customer.serviceStartDate} />
        <InfoRow icon={<User size={14} />} label="Months Active" value={`${customer.monthsActive} months`} />
        <InfoRow icon={<User size={14} />} label="Assigned Technician" value={customer.assignedTechnician || 'Unassigned'} />
      </div>

      {/* Lock-in tracker */}
      <div className="bg-secondary rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Lock size={14} className="text-primary" />
            <span className="text-sm font-600 text-foreground">36-Month Lock-in Contract</span>
          </div>
          <span className="text-sm font-700 text-primary tabular-nums">{customer.lockInMonths}/36 months</span>
        </div>
        <div className="w-full bg-white rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${lockInPct}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          {lockInRemaining > 0
            ? `${lockInRemaining} months remaining in lock-in period`
            : 'Lock-in period completed'}
        </p>
      </div>

      {/* Notes */}
      {customer.notes && (
        <div className="bg-muted rounded-lg p-3 mb-4">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
          <p className="text-sm text-foreground">{customer.notes}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <button className="flex-1 py-2 text-sm font-600 btn-primary rounded-lg">Edit Profile</button>
        <button className="flex-1 py-2 text-sm font-600 btn-secondary rounded-lg">Service History</button>
        <button className="px-4 py-2 text-sm font-600 bg-card border border-border rounded-lg hover:bg-muted transition-colors">
          Schedule Job
        </button>
      </div>
    </Modal>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
      <div>
        <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-sm text-foreground mt-0.5">{value}</p>
      </div>
    </div>
  );
}